/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import React, { ComponentProps, useContext } from 'react';

import { makeStyles, Theme, darken, lighten } from '@mui/material/styles';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { Input as CoreInput, IInputProps as ICoreInputProps, InputComponentType, OmitInputProps } from '@ngt/forms-core';

import { useFieldErrors } from '../../../hooks/form/useFieldErrors';
import FormErrorDisplayMode from '../../../types/form/FormErrorDisplayMode';
import { FormError } from '../../../errors';
import FormErrorType from '../../../types/form/FormErrorType';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */
export interface IInputProps<
    TComponent extends InputComponentType = any,
    TFieldValue = any,
    TFormValue extends object = any,
    TError = FormError,
    TInputValue = TFieldValue
> extends ICoreInputProps<TComponent, TFieldValue, TFormValue, TError, TInputValue> {
    disabled?: boolean;
    errorMode?: FormErrorDisplayMode;
}

/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

export interface IAdditionalFormInputProps {
    disabled: boolean;
    hasErrors: boolean;
    errors: FormError[];
    errorType?: FormErrorType;
    label?: string;
}

const Input = <
    TComponent extends InputComponentType = any,
    TFieldValue = any,
    TFormValue extends object = any,
    TError = FormError
>({
    
    disabled,
    errorMode,
    label,
    ...props
}: IInputProps<TComponent, TFieldValue,TFormValue, TError> & Partial<OmitInputProps<ComponentProps<TComponent>>>) => {
    const inputDisabled = disabled;

    const errors = useFieldErrors(errorMode);

    return (
        <CoreInput
            disabled={inputDisabled}
            hasErrors={errors.hasErrors}
            errors={errors.errors}
            errorType={errors.type}
            label={label}
            {...props as any}
        />
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default Input;
